import { useId } from 'react';

/**
 * Clinical triage clipboard illustration — the single approved graphic for empty-data states
 * across the product (see docs/mocks/empty-states/5-empty-triage-clipboard.svg).
 * Gradient/filter/pattern ids are namespaced per-instance so multiple EmptyState renders on one
 * page never clash.
 *
 * The float/pulse motion reads as a nice detail when a page shows a single empty state, but
 * looks busy when several render side by side (e.g. a grid of category cards) — pass
 * `animated={false}` for those spots to render the same illustration static.
 */
export function EmptyStateIllustration({ className, animated = true }: { className?: string; animated?: boolean }) {
  // useId() can contain ':' which is illegal in a CSS class/keyframe name — strip to alnum only.
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const brand = `clin-brand-${uid}`;
  const metal = `clin-metal-${uid}`;
  const grid = `clinical-grid-${uid}`;
  const shadow = `clin-shadow-${uid}`;
  const glow = `clin-glow-${uid}`;

  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 180" width="100%" height="100%" fill="none" className={className}>
      <defs>
        <linearGradient id={brand} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#10b981" />
          <stop offset="100%" stopColor="#047857" />
        </linearGradient>

        <linearGradient id={metal} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#6ee7b7" />
          <stop offset="50%" stopColor="#059669" />
          <stop offset="100%" stopColor="#047857" />
        </linearGradient>

        <pattern id={grid} width="8" height="8" patternUnits="userSpaceOnUse">
          <path d="M 8 0 L 0 0 0 8" fill="none" stroke="#10b981" strokeOpacity="0.12" strokeWidth="0.5" />
        </pattern>

        <filter id={shadow} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="8" stdDeviation="12" floodColor="#047857" floodOpacity="0.18" />
        </filter>

        <filter id={glow} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#10b981" floodOpacity="0.25" />
        </filter>
      </defs>

      {animated && (
        <style>{`
          .board-float-${uid} { animation: clinBoardFloat-${uid} 4s ease-in-out infinite; transform-origin: 110px 90px; }
          .steth-wrap-${uid} { animation: stethPulse-${uid} 4s ease-in-out infinite; transform-origin: 165px 110px; }

          @keyframes clinBoardFloat-${uid} {
            0%, 100% { transform: translateY(0) rotate(0deg); }
            50% { transform: translateY(-5px) rotate(-0.5deg); }
          }
          @keyframes stethPulse-${uid} {
            0%, 100% { transform: translateY(0) scale(1); }
            50% { transform: translateY(-3px) scale(1.01); }
          }
        `}</style>
      )}

      <circle cx="115" cy="90" r="72" fill="none" stroke="#10b981" strokeOpacity="0.12" strokeWidth="1" strokeDasharray="4 4" />

      <g className={animated ? `board-float-${uid}` : undefined}>
        <rect x="58" y="16" width="112" height="148" rx="10" fill="var(--surface, #ffffff)" stroke="#10b981" strokeWidth="2" filter={`url(#${shadow})`} />

        <path d="M84 16 V8 C84 5 87 2 90 2 H138 C141 2 144 5 144 8 V16 Z" fill={`url(#${metal})`} />
        <path d="M112 6 h4 v3 h3 v4 h-3 v3 h-4 v-3 h-3 v-4 h3 z" fill="var(--surface, #ffffff)" />

        <rect x="65" y="28" width="98" height="128" rx="4" fill="var(--surface, #ffffff)" />
        <rect x="65" y="28" width="98" height="128" rx="4" fill={`url(#${grid})`} />

        <text x="73" y="44" fontFamily="'IBM Plex Mono', monospace, serif" fontSize="13" fontWeight="700" fill="#047857">Rx</text>

        <line x1="92" y1="38" x2="152" y2="38" stroke="#059669" strokeWidth="2" strokeLinecap="round" />
        <line x1="92" y1="44" x2="135" y2="44" stroke="currentColor" strokeOpacity="0.2" strokeWidth="1.5" strokeLinecap="round" />

        <g transform="translate(72, 52)">
          <rect x="0" y="0" width="84" height="15" rx="3" fill="#047857" opacity="0.08" />
          <rect x="0" y="0" width="84" height="15" rx="3" stroke="#10b981" strokeOpacity="0.25" strokeWidth="0.8" fill="none" />

          <path d="M4 7.5 h3 l2 -4 l2 8 l2 -4 h3" stroke="#10b981" strokeWidth="1" fill="none" />
          <text x="18" y="11" fontFamily="'IBM Plex Mono', monospace" fontSize="7.5" fontWeight="600" fill="var(--text, #0e1a16)">-- bpm</text>

          <text x="52" y="11" fontFamily="'IBM Plex Mono', monospace" fontSize="7.5" fontWeight="600" fill="#059669">SpO₂ --%</text>
        </g>

        <line x1="72" y1="74" x2="156" y2="74" stroke="#10b981" strokeOpacity="0.25" strokeWidth="1" strokeDasharray="3 3" />

        <g transform="translate(73, 84)">
          <rect x="0" y="0" width="13" height="13" rx="3" fill="none" stroke="#10b981" strokeDasharray="2 2" strokeWidth="1.2" />
          <circle cx="21" cy="6.5" r="2.5" fill="#10b981" />
          <line x1="28" y1="6.5" x2="72" y2="6.5" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" strokeLinecap="round" />
        </g>

        <g transform="translate(73, 102)">
          <rect x="0" y="0" width="13" height="13" rx="3" fill="none" stroke="#10b981" strokeDasharray="2 2" strokeWidth="1.2" />
          <circle cx="21" cy="6.5" r="2.5" fill="#f59e0b" />
          <line x1="28" y1="6.5" x2="65" y2="6.5" stroke="currentColor" strokeOpacity="0.15" strokeWidth="2" strokeLinecap="round" />
        </g>

        <g transform="translate(73, 120)">
          <rect x="0" y="0" width="13" height="13" rx="3" fill="none" stroke="currentColor" strokeOpacity="0.2" strokeDasharray="2 2" strokeWidth="1.2" />
          <circle cx="21" cy="6.5" r="2.5" fill="#06b6d4" />
          <line x1="28" y1="6.5" x2="55" y2="6.5" stroke="currentColor" strokeOpacity="0.1" strokeWidth="2" strokeLinecap="round" />
        </g>

        <g transform="translate(73, 138)">
          <rect x="0" y="0" width="13" height="13" rx="3" fill="none" stroke="currentColor" strokeOpacity="0.15" strokeDasharray="2 2" strokeWidth="1" />
          <line x1="21" y1="6.5" x2="45" y2="6.5" stroke="currentColor" strokeOpacity="0.08" strokeWidth="2" strokeLinecap="round" />
        </g>
      </g>

      <g className={animated ? `steth-wrap-${uid}` : undefined}>
        <path d="M152 62 C175 52 188 70 180 88 C174 102 162 110 168 130" stroke="#059669" strokeWidth="3" strokeLinecap="round" fill="none" filter={`url(#${shadow})`} />
        <path d="M154 66 C172 58 182 72 176 88 C171 100 160 108 165 126" stroke="#6ee7b7" strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.6" />

        <circle cx="152" cy="62" r="2.5" fill="#047857" />
        <circle cx="154" cy="66" r="2.5" fill="#047857" />

        <g transform="translate(168, 130)">
          <circle cx="0" cy="0" r="13" fill="var(--surface, #ffffff)" stroke="#047857" strokeWidth="2" filter={`url(#${glow})`} />
          <circle cx="0" cy="0" r="9" fill={`url(#${brand})`} />
          <circle cx="0" cy="0" r="4" fill="var(--surface, #ffffff)" />
        </g>
      </g>
    </svg>
  );
}
